import React from 'react';

import { VStack, HStack, Text, Center, Spinner, Box } from '@chakra-ui/react';

import PaginationControls from 'components/table/PaginationControls';
import ResultCard from './ResultCard';
import { useColors } from './colors';

const ResultsPanel = ({
  results,
  totalCount,
  currentPage,
  totalPages,
  isLoading,
  hasSearched,
  searchField,
  imageIndexByProduct,
  onPageChange,
  onPrevImage,
  onNextImage,
  onSelectImage,
}) => {
  const { textColor, secondaryTextColor } = useColors();

  if (isLoading) {
    return (
      <Center py={16}>
        <VStack spacing={4}>
          <Spinner size="lg" color="blue.400" thickness="3px" />
          <Text color={secondaryTextColor}>Searching latest inventory files…</Text>
        </VStack>
      </Center>
    );
  }

  if (!hasSearched) {
    return (
      <Center py={16}>
        <VStack spacing={2} maxW="md" textAlign="center">
          <Text fontSize="lg" fontWeight="semibold" color={textColor}>
            Search supplier inventory
          </Text>
          <Text fontSize="sm" color={secondaryTextColor}>
            Enter a SKU, partslink, OEM, or description. Results come from each supplier&apos;s
            most recent chunked inventory file.
          </Text>
        </VStack>
      </Center>
    );
  }

  if (results.length === 0) {
    return (
      <Center py={16}>
        <VStack spacing={2}>
          <Text fontSize="lg" fontWeight="semibold" color={textColor}>
            No matches found
          </Text>
          <Text fontSize="sm" color={secondaryTextColor}>
            Try a different term, switch the field filter, or broaden the supplier selection.
          </Text>
        </VStack>
      </Center>
    );
  }

  const fieldLabel =
    searchField === 'all'
      ? 'all fields'
      : searchField.replace(/([A-Z])/g, ' $1').toLowerCase();

  return (
    <VStack spacing={5} align="stretch">
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <Box>
          <Text fontSize="lg" fontWeight="semibold" color={textColor}>
            {totalCount.toLocaleString()} result{totalCount === 1 ? '' : 's'}
          </Text>
          <Text fontSize="sm" color={secondaryTextColor}>
            Matched in {fieldLabel}
          </Text>
        </Box>
        {totalPages > 1 && (
          <Text fontSize="sm" color={secondaryTextColor}>
            Page {currentPage} of {totalPages}
          </Text>
        )}
      </HStack>

      <VStack spacing={4} align="stretch">
        {results.map((product, index) => {
          const productId =
            product.supplierIdentifier ||
            product.oem ||
            product.partslink ||
            `product-${index}`;
          return (
            <ResultCard
              key={`${productId}-${product.inventoryId}-${index}`}
              product={product}
              index={index}
              imageIndex={imageIndexByProduct[productId] || 0}
              onPrevImage={onPrevImage}
              onNextImage={onNextImage}
              onSelectImage={onSelectImage}
            />
          );
        })}
      </VStack>

      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </VStack>
  );
};

export default ResultsPanel;
